<template>
  <span>
    <v-row>
      <v-col cols="12">
        <v-data-table
          id="cruise-data-table"
          :items="cruiseData"
          :headers="headers"
          :hide-default-footer="cruiseData.length < 10"
          :footer-props="{ 'items-per-page-options': dataTableItemsPerPage }"
          :items-per-page="10"
          dense
          :no-data-text="$t('noCruiseData')"
        >
          <template #item.pricePerUnit="{ item }">
            <span>{{ formatMoney(item.pricePerUnit) }}</span>
          </template>
          <template #item.pricePerMBF="{ item }">
            <span>{{ formatMoney(item.pricePerMBF) }}</span>
          </template>

          <template #item.cruisedValue="{ item }">
            <span>{{ formatMoney(item.cruisedValue) }}</span>
          </template>

          <template #item.harvestedTons="{ item }">
            <span>{{ numberWithCommas(item.harvestedTons, 3) }}</span>
          </template>
          <template #item.harvestedMBF="{ item }">
            <span>{{ numberWithCommas(item.harvestedMBF, 3) }}</span>
          </template>

          <template #item.adjustmentTons="{ item }">
            <span>{{ numberWithCommas(item.adjustmentTons, 3) }}</span>
          </template>

          <template #item.harvested="{ item }">
            <span>{{ numberWithCommas(item.harvested / 1000, 3) }}</span>
          </template>
          <template #item.adjustment="{ item }">
            <span>{{ numberWithCommas(item.adjustment / 1000, 3) }}</span>
          </template>
          <template #item.adjustmentMBF="{ item }">
            <span>{{ numberWithCommas(item.adjustmentMBF, 3) }}</span>
          </template>

          <template #item.remainingTons="{ item }">
            <span>{{ numberWithCommas(item.remainingTons, 3) }}</span>
          </template>
          <template #item.remainingMBF="{ item }">
            <span>{{ numberWithCommas(item.remainingMBF, 3) }}</span>
          </template>

          <template #item.expectedTons="{ item }">
            <span>{{ numberWithCommas(item.expectedTons, 3) }}</span>
          </template>

          <template #item.remaining="{ item }">
            <span>{{ numberWithCommas(item.remaining / 1000, 3) }}</span>
          </template>
          <template #item.expectedMBF="{ item }">
            <span>{{ numberWithCommas(item.expectedMBF, 3) }}</span>
          </template>

          <template #item.expected="{ item }">
            <span>{{ numberWithCommas(item.expected / 1000, 3) }}</span>
          </template>

          <template #item.obligationRate="{ item }">
            <span>{{ formatMoney(item.obligationRate) }}</span>
          </template>
          <template #item.obligation="{ item }">
            <span>{{ formatMoney(item.obligation / 1000) }}</span>
          </template>
          <template #item.obligationAccrued="{ item }">
            <span>{{ formatMoney(item.obligationAccrued) }}</span>
          </template>
          <template #item.obligationBalance="{ item }">
            <span>{{ formatMoney(item.obligationBalance) }}</span>
          </template>

          <template #item.actions="{ item }">
            <Icon
            v-if="isVolumeBased"
            icon="mdi-map-marker-multiple"
            iconColor="primary"
            elementId="cruise-allocate-button"
            :tooltipText="$t('allocate')"
            @icon-clicked="$emit('allocate', item)"
            />
            <Icon
            icon="mdi-pencil"
            iconColor="success"
            elementId="cruise-edit-button"
            :tooltipText="$t('edit')"
            @icon-clicked="editCruiseData(item)"
            />
            <Icon
            icon="mdi-delete-forever"
            iconColor="error"
            elementId="cruise-delete-button"
            :tooltipText="$t('delete')"
            @icon-clicked="deleteCruiseData(item)"
            />
          </template>
          <template #body.append v-if="cruiseData.length > 1">
            <tr v-if="!isVolumeBased" class="font-weight-bold" style="border: solid thin;">
              <td class="text-left">{{$t('totals')}}</td>
              <td class="text-right">{{totals.harvested}}</td>
              <td class="text-right">{{totals.adjustment}}</td>
              <td class="text-right">{{totals.remaining}}</td>
              <td class="text-right">{{totals.expected}}</td>
              <td class="text-right">---</td>
              <td class="text-right">{{formatMoney(totals.cruisedValue)}}</td>
              <td class="text-right"></td>
            </tr>
            <tr v-else class="font-weight-bold" style="border: solid thin;">
              <td class="text-left">{{$t('totals')}}</td>
              <td class="text-right">{{volumeTotals.harvested}}</td>
              <td class="text-right">{{volumeTotals.adjustment}}</td>
              <td class="text-right">{{volumeTotals.remaining}}</td>
              <td class="text-right">{{volumeTotals.expected}}</td>
              <td class="text-right">---</td>
              <td v-if="showObligation" class="text-right">---</td>
              <td v-if="showObligation" class="text-right">{{formatMoney(volumeTotals.obligation)}}</td>
              <td v-if="showObligationAccrued" class="text-right">{{formatMoney(volumeTotals.obligationAccrued)}}</td>
              <td v-if="showObligation" class="text-right">{{formatMoney(volumeTotals.obligationBalance)}}</td>
              <td class="text-right">{{formatMoney(volumeTotals.cruisedValue)}}</td>
              <td class="text-right"></td>
            </tr>
          </template>
          <template #top>
            <slot name="top"/>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <slot name="bottom"/>
      </v-col>
    </v-row>
  </span>
</template>

<script>
import CruiseDataHeaders from '@/headers/Composition'
import { mapGetters } from 'vuex'
import { formatMoney, tonStringFromPounds, numberWithCommas } from '@/utils/NumericMutations'
import { cruiseDataTotals, volumeBasedCruiseDataTotals } from '@/components/tract/cruise-data/CruiseDataTotals'
import { dataTableItemsPerPage } from '@/utils/componentHelpers'
import { TractTypeSpecialization } from '@/utils/Enumerations'

export default {
  name: 'CruiseDataTable',

  props: {
    propCruiseData: {
      type: Array,
      default: undefined
    },
    units: {
      required: true,
      type: Object
    },
    isVolumeBased: {
      type: Boolean
    },
    tractTypeSpecialization: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dataTableItemsPerPage
  }),

  components: {
    Icon: () => import('../../helper/Icon.vue')
  },

  computed: {
    ...mapGetters('tract-contents', ['allTractContents']),

    cruiseData () {
      return this.propCruiseData ?? this.allTractContents
    },

    showObligation () {
      return this.tractTypeSpecialization === TractTypeSpecialization.ObligationUncapped
    },

    showObligationAccrued () {
      return this.tractTypeSpecialization === TractTypeSpecialization.ObligationUncapped ||
        this.tractTypeSpecialization === TractTypeSpecialization.ObligationCapped
    },

    totals () {
      return cruiseDataTotals(this.cruiseData, this.units)
    },

    volumeTotals () {
      return volumeBasedCruiseDataTotals(this.cruiseData)
    },

    headers () {
      const headers = this.isVolumeBased
        ? CruiseDataHeaders.volumeHeaders(this.showObligation, this.showObligationAccrued)
        : CruiseDataHeaders.compositionHeaders(this.units)
      return headers
    }
  },

  methods: {
    formatMoney,
    tonStringFromPounds,
    numberWithCommas,

    deleteCruiseData (cruiseData) {
      this.$emit('delete', cruiseData)
    },

    editCruiseData (cruiseData) {
      this.$emit('edit', cruiseData)
    }
  }
}
</script>
